import { PROVIDER_CATALOG, type ProviderView } from '@cyberscore/contracts';

/** Pure table logic for the providers list (search, filters, sort, groups, pages, CSV), kept apart from
 * React so it can be tested directly. */

export type ColumnId =
  | 'provider'
  | 'endpoint_model'
  | 'api_key'
  | 'hosted'
  | 'status'
  | 'kind'
  | 'endpoint'
  | 'model'
  | 'thinking'
  | 'structured_output'
  | 'price_input'
  | 'price_output'
  | 'enabled'
  | 'last_checked'
  | 'latency'
  | 'updated';

export interface Column {
  id: ColumnId;
  label: string;
  /** Plain text for CSV and for sorting. */
  text: (p: ProviderView) => string;
  sort?: (p: ProviderView) => string | number;
  align?: 'right';
}

export const STATUS_LABEL: Record<ProviderView['status'], string> = {
  connected: 'Connected',
  failed: 'Failed',
  untested: 'Not tested',
};
export const HOSTED_LABEL = { external: 'External', self_hosted: 'Self-hosted' } as const;

const kindLabel = (p: ProviderView) => PROVIDER_CATALOG[p.kind].label;
const usd = (n: number) => (n ? `$${n.toFixed(2)}` : '—');
const thinking = (p: ProviderView) => (p.reasoning === null ? 'Model default' : p.reasoning ? 'On' : 'Off');

export const COLUMNS: Column[] = [
  { id: 'provider', label: 'Provider', text: (p) => p.name, sort: (p) => p.name.toLowerCase() },
  {
    id: 'endpoint_model',
    label: 'Endpoint / Model',
    text: (p) => `${p.endpoint} ${p.model}`,
    sort: (p) => p.model.toLowerCase(),
  },
  { id: 'api_key', label: 'API key', text: (p) => p.key_hint ?? '' },
  { id: 'hosted', label: 'Hosted', text: (p) => HOSTED_LABEL[p.hosted], sort: (p) => p.hosted },
  {
    id: 'status',
    label: 'Status',
    text: (p) => (p.enabled ? STATUS_LABEL[p.status] : 'Disabled'),
    sort: (p) => (p.enabled ? ['connected', 'untested', 'failed'].indexOf(p.status) : 9),
  },
  { id: 'kind', label: 'Provider type', text: kindLabel, sort: kindLabel },
  { id: 'endpoint', label: 'Endpoint', text: (p) => p.endpoint },
  { id: 'model', label: 'Model', text: (p) => p.model, sort: (p) => p.model.toLowerCase() },
  { id: 'thinking', label: 'Thinking', text: thinking },
  {
    id: 'structured_output',
    label: 'Structured output',
    text: (p) => (p.structured_output === 'json_schema' ? 'JSON schema' : 'Function calling'),
  },
  {
    id: 'price_input',
    label: 'Input $/1M',
    text: (p) => usd(p.price_input_per_mtok),
    sort: (p) => p.price_input_per_mtok,
    align: 'right',
  },
  {
    id: 'price_output',
    label: 'Output $/1M',
    text: (p) => usd(p.price_output_per_mtok),
    sort: (p) => p.price_output_per_mtok,
    align: 'right',
  },
  {
    id: 'enabled',
    label: 'Enabled',
    text: (p) => (p.enabled ? 'Yes' : 'No'),
    sort: (p) => (p.enabled ? 0 : 1),
  },
  {
    id: 'last_checked',
    label: 'Last checked',
    text: (p) => (p.last_check ? formatDate(p.last_check.checked_at) : 'Never'),
    sort: (p) => p.last_check?.checked_at ?? '',
  },
  {
    id: 'latency',
    label: 'Latency',
    text: (p) => (p.last_check?.latency_ms != null ? `${p.last_check.latency_ms} ms` : '—'),
    sort: (p) => p.last_check?.latency_ms ?? Number.MAX_SAFE_INTEGER,
    align: 'right',
  },
  {
    id: 'updated',
    label: 'Updated',
    text: (p) => `${formatDate(p.updated_at)}${p.updated_by_name ? ` by ${p.updated_by_name}` : ''}`,
    sort: (p) => p.updated_at,
  },
];

export const DEFAULT_COLUMNS: ColumnId[] = ['provider', 'endpoint_model', 'api_key', 'hosted', 'status'];

export type FilterKey = 'kind' | 'hosted' | 'status' | 'enabled';
export type Filters = Partial<Record<FilterKey, string[]>>;

export const FILTER_GROUPS: { key: FilterKey; label: string; options: { value: string; label: string }[] }[] =
  [
    {
      key: 'kind',
      label: 'Provider type',
      options: Object.entries(PROVIDER_CATALOG).map(([value, i]) => ({ value, label: i.label })),
    },
    {
      key: 'hosted',
      label: 'Hosted',
      options: Object.entries(HOSTED_LABEL).map(([value, label]) => ({ value, label })),
    },
    {
      key: 'status',
      label: 'Status',
      options: Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label })),
    },
    {
      key: 'enabled',
      label: 'Enabled',
      options: [
        { value: 'yes', label: 'Enabled' },
        { value: 'no', label: 'Disabled' },
      ],
    },
  ];

const filterValue: Record<FilterKey, (p: ProviderView) => string> = {
  kind: (p) => p.kind,
  hosted: (p) => p.hosted,
  status: (p) => p.status,
  enabled: (p) => (p.enabled ? 'yes' : 'no'),
};

export function filterRows(rows: ProviderView[], search: string, filters: Filters): ProviderView[] {
  const q = search.trim().toLowerCase();
  return rows.filter((p) => {
    if (q) {
      const hay = [p.name, kindLabel(p), p.model, p.endpoint, HOSTED_LABEL[p.hosted]].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return (Object.keys(filters) as FilterKey[]).every((k) => {
      const wanted = filters[k];
      return !wanted?.length || wanted.includes(filterValue[k](p));
    });
  });
}

export function activeFilterCount(filters: Filters): number {
  return Object.values(filters).reduce((n, v) => n + (v?.length ?? 0), 0);
}

export interface Sort {
  column: ColumnId;
  dir: 'asc' | 'desc';
}

/** The default provider first, then the chosen order. */
export function sortRows(rows: ProviderView[], sort: Sort | null): ProviderView[] {
  const col = COLUMNS.find((c) => c.id === sort?.column);
  const key = col?.sort ?? col?.text;
  return [...rows].sort((a, b) => {
    if (!sort || !key) return Number(b.is_default) - Number(a.is_default) || a.name.localeCompare(b.name);
    const x = key(a),
      y = key(b);
    const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return sort.dir === 'asc' ? c : -c;
  });
}

export type GroupBy = 'none' | 'kind' | 'hosted' | 'status';
export const GROUP_LABEL: Record<GroupBy, string> = {
  none: 'No grouping',
  kind: 'Provider type',
  hosted: 'Hosted',
  status: 'Status',
};

export function groupRows(
  rows: ProviderView[],
  by: GroupBy,
): { label: string | null; rows: ProviderView[] }[] {
  if (by === 'none') return [{ label: null, rows }];
  const label = (p: ProviderView) =>
    by === 'kind' ? kindLabel(p) : by === 'hosted' ? HOSTED_LABEL[p.hosted] : STATUS_LABEL[p.status];
  const groups = new Map<string, ProviderView[]>();
  for (const p of rows) groups.set(label(p), [...(groups.get(label(p)) ?? []), p]);
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([l, r]) => ({ label: l, rows: r }));
}

export type PageSize = 10 | 25 | 50 | 'all';

export function paginate<T>(rows: T[], page: number, size: PageSize) {
  const total = rows.length;
  if (size === 'all' || total === 0)
    return { rows, page: 1, pages: 1, from: total ? 1 : 0, to: total, total };
  const pages = Math.max(1, Math.ceil(total / size));
  const p = Math.min(Math.max(1, page), pages);
  const start = (p - 1) * size;
  return {
    rows: rows.slice(start, start + size),
    page: p,
    pages,
    from: start + 1,
    to: Math.min(total, start + size),
    total,
  };
}

/** CSV of what is on screen. Keys are never in the data; only their hints. */
export function toCsv(rows: ProviderView[], columns: ColumnId[]): string {
  const cols = columns
    .flatMap((id) => (id === 'endpoint_model' ? (['endpoint', 'model'] as ColumnId[]) : [id]))
    .map((id) => COLUMNS.find((c) => c.id === id)!)
    .filter(Boolean);
  const cell = (v: string) => {
    // Quote when needed; prefix formula characters so spreadsheets don't execute them.
    const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
    return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  const lines = [cols.map((c) => cell(c.label)), ...rows.map((p) => cols.map((c) => cell(c.text(p))))];
  return lines.map((l) => l.join(',')).join('\r\n') + '\r\n';
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-MY', { dateStyle: 'medium', timeStyle: 'short' });
}
